import { Link } from '@tanstack/react-router';
import { ArrowLeft, CalendarDays, Clock, Mail } from 'lucide-react';
import type { ReactNode } from 'react';
import { Pill } from '@/shared/components';
import { formatDate, formatDateTime } from '@/shared/lib/format';
import type { UserResponse } from '@/shared/api';
import {
  fullName,
  roleLabel,
  USER_STATUS_LABELS,
  USER_STATUS_PILL,
  userRole,
  userStatus,
} from '../userStatus';

/**
 * Kopf der Benutzerakte — Rückweg, Name, Zustand und die Metazeile.
 *
 * <p>Gleiche Bauart wie `BookingDetailHeader`: der Rückweg steht über dem Titel und nicht am
 * Seitenende, Zustand und Rolle stehen als Pille neben dem Namen, darunter die wenigen
 * Metadaten, die man beim Hereinkommen sucht.</p>
 */
export function UserDetailHeader({ user }: { user: UserResponse }) {
  const status = userStatus(user);
  return (
    <div className="bg-card border-line/50 shadow-card rounded-lg border px-4 py-4 sm:px-6 sm:py-5">
      <Link
        to="/users"
        className="text-ink-soft hover:text-ink mb-3 inline-flex min-h-11 items-center gap-1 text-xs font-medium transition-colors"
      >
        <ArrowLeft size={14} strokeWidth={2} aria-hidden />
        Alle Benutzer
      </Link>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-ink max-w-full break-words font-serif text-2xl font-medium tracking-tight sm:text-3xl">
          {fullName(user)}
        </h1>
        <Pill variant={USER_STATUS_PILL[status]} size="md">
          {USER_STATUS_LABELS[status]}
        </Pill>
        <Pill variant="default" size="md">
          {roleLabel(userRole(user))}
        </Pill>
      </div>

      <div className="text-ink-soft mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <MetaItem icon={<Mail size={14} strokeWidth={2} />}>
          <span className="wrap-break-word">{user.email}</span>
        </MetaItem>
        <MetaItem icon={<CalendarDays size={14} strokeWidth={2} />}>
          Angelegt am {formatDate(user.createdAt)}
        </MetaItem>
        <MetaItem icon={<Clock size={14} strokeWidth={2} />}>
          {user.lastLoginAt
            ? `Zuletzt angemeldet am ${formatDateTime(user.lastLoginAt)}`
            : 'Noch nie angemeldet'}
        </MetaItem>
      </div>
    </div>
  );
}

function MetaItem({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className="text-ink-faint shrink-0">{icon}</span>
      {children}
    </span>
  );
}
